import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { ArrowLeft, ArrowRight } from "lucide-react";

import { SiteHeader, SiteFooter } from "@/components/site-chrome";
import { Breadcrumbs } from "@/components/breadcrumbs";
import { Button } from "@/components/ui/button";
import type { Resource, Section } from "@/content/resources";
import { getResource, resources } from "@/content/resources";

import { SITE_URL as SITE, BRAND_OG_IMAGE, BRAND_OG_IMAGE_ALT, SITE_NAME } from "@/lib/seo";

export const Route = createFileRoute("/resources/$slug")({
  loader: ({ params }) => {
    const resource = getResource(params.slug);
    if (!resource) throw notFound();
    return { resource };
  },
  head: ({ params, loaderData }) => {
    const r = loaderData?.resource;
    if (!r) return {};
    const url = `${SITE}/resources/${params.slug}`;
    return {
      meta: [
        { title: r.metaTitle },
        { name: "description", content: r.description },
        { property: "og:title", content: r.metaTitle },
        { property: "og:description", content: r.description },
        { property: "og:type", content: "article" },
        { property: "og:url", content: url },
        { property: "og:site_name", content: SITE_NAME },
        { property: "og:image", content: BRAND_OG_IMAGE },
        { property: "og:image:alt", content: BRAND_OG_IMAGE_ALT },
        { name: "twitter:card", content: "summary_large_image" },
        { name: "twitter:image", content: BRAND_OG_IMAGE },
        { name: "twitter:image:alt", content: BRAND_OG_IMAGE_ALT },
      ],
      links: [{ rel: "canonical", href: url }],
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify(
            r.kind === "qa" && r.question && r.shortAnswer
              ? {
                  "@context": "https://schema.org",
                  "@type": "FAQPage",
                  mainEntity: [
                    {
                      "@type": "Question",
                      name: r.question,
                      acceptedAnswer: { "@type": "Answer", text: r.shortAnswer },
                    },
                  ],
                }
              : {
                  "@context": "https://schema.org",
                  "@type": "Article",
                  headline: r.title,
                  description: r.description,
                  dateModified: r.updated,
                  mainEntityOfPage: url,
                  publisher: { "@type": "Organization", name: "Smart Word Labs" },
                },
          ),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              { "@type": "ListItem", position: 1, name: "Home", item: SITE },
              {
                "@type": "ListItem",
                position: 2,
                name: "Parent Resources",
                item: `${SITE}/resources`,
              },
              { "@type": "ListItem", position: 3, name: r.title },
            ],
          }),
        },
      ],
    };
  },
  component: ResourceArticle,
});

function ResourceArticle() {
  const { resource } = Route.useLoaderData() as { resource: Resource };
  const related = resources.filter((r) => r.slug !== resource.slug).slice(0, 3);

  return (
    <div className="min-h-screen bg-background">
      <SiteHeader />
      <Breadcrumbs
        trail={[
          { name: "Parent Resources", path: "/resources" },
          { name: resource.title, path: `/resources/${resource.slug}` },
        ]}
      />

      <main className="mx-auto max-w-3xl px-5 py-12">
        <Link
          to="/resources"
          className="inline-flex items-center gap-1.5 text-sm font-medium text-muted-foreground hover:text-primary"
        >
          <ArrowLeft className="size-4" /> All parent resources
        </Link>

        <article className="mt-8">
          <span className="text-xs font-semibold uppercase tracking-wide text-accent-foreground/80">
            {resource.topic} · {resource.readMinutes} min read
          </span>
          <h1 className="mt-3 font-display text-3xl font-extrabold leading-tight text-primary sm:text-4xl">
            {resource.title}
          </h1>
          <p className="mt-4 text-lg text-muted-foreground">{resource.description}</p>

          {resource.shortAnswer ? (
            <div className="mt-8 rounded-2xl border border-border bg-muted/40 p-6">
              <p className="text-xs font-semibold uppercase tracking-wide text-primary">
                Short answer
              </p>
              <p className="mt-2 leading-relaxed text-foreground">{resource.shortAnswer}</p>
            </div>
          ) : null}

          <div className="mt-10 space-y-10">
            {resource.sections.map((s: Section) => (
              <section key={s.heading}>
                <h2 className="font-display text-xl font-bold text-primary sm:text-2xl">
                  {s.heading}
                </h2>
                {s.paragraphs.map((p: string) => (
                  <p key={p} className="mt-4 leading-relaxed text-muted-foreground">
                    {p}
                  </p>
                ))}
                {s.bullets ? (
                  <ul className="mt-4 space-y-2">
                    {s.bullets.map((b: string) => (
                      <li
                        key={b}
                        className="flex gap-3 leading-relaxed text-muted-foreground before:mt-2.5 before:size-1.5 before:shrink-0 before:rounded-full before:bg-accent"
                      >
                        {b}
                      </li>
                    ))}
                  </ul>
                ) : null}
              </section>
            ))}
          </div>
        </article>

        <div className="gradient-ink mt-14 rounded-2xl px-8 py-10 text-center text-primary-foreground">
          <h2 className="font-display text-2xl font-extrabold text-primary-foreground">
            Not sure where your child is?
          </h2>
          <p className="mx-auto mt-3 max-w-md text-primary-foreground/75">
            Book a free diagnostic call and we will place your child properly before you spend
            anything.
          </p>
          <Button variant="hero" size="xl" className="mt-7" asChild>
            <Link to="/auth" search={{ mode: "signup" }}>
              Book a free assessment
            </Link>
          </Button>
        </div>

        <section className="mt-14">
          <h2 className="font-display text-xl font-bold text-primary">Keep reading</h2>
          <ul className="mt-5 space-y-3">
            {related.map((r) => (
              <li key={r.slug}>
                <Link
                  to="/resources/$slug"
                  params={{ slug: r.slug }}
                  className="flex items-center justify-between gap-4 rounded-xl border border-border bg-card px-5 py-4 text-sm font-medium text-primary transition-colors hover:bg-muted/50"
                >
                  {r.title}
                  <ArrowRight className="size-4 shrink-0" />
                </Link>
              </li>
            ))}
          </ul>
        </section>
      </main>

      <SiteFooter />
    </div>
  );
}
